import { computed } from 'mobx';
import { Instance, types } from 'mobx-state-tree';
import { IEntityStore, IItemCustomization, ISupplier, ISupplierItem, ITemplate, ITemplateItem } from '../../main/entities';
import { transl } from '../../main/transl';
import { mg } from '../../mg/mg';
import { getOmnistore, IOmnistore } from '../../stores/Omnistore';
import { adOrderingStore, IAdOrderingItem } from '../ads/AdsOrderingStore';
import { TemplDetailsStore } from './templ/TemplDetailsStore';

class CombiItemHelper {
	constructor(
		public supplierRepo: IEntityStore<ISupplier>,
		public templateRepo: IEntityStore<ITemplate>,
		private itemCustomizationRepo: IEntityStore<IItemCustomization>,
		private locationStore: {branchId: number | undefined}) {
	}

	@computed get _itemCustomizations(): Map<number, IItemCustomization> {
		return new Map(this.itemCustomizationRepo.allAsList
			.filter(o => o.branchId === this.locationStore.branchId)
			.map(o => [o.supplierItemId, o]));
	}
}

class CombiItem {
	constructor(
		private _templateItem: ITemplateItem | undefined,
		private _supplierItem: ISupplierItem | undefined,
		private _adOrderingItem: IAdOrderingItem | undefined,
		private _helper: CombiItemHelper) {
		if (!_templateItem && !_supplierItem) throw new Error('Missing both templateItem and supplierItem');
	}

	@computed get id() {
		return this._templateItem ? this._templateItem.id
			: this._supplierItem ? -this._supplierItem.id
				: 0;
	}

	@computed get supplierItemId() {
		return this._templateItem ? this._templateItem.supplierItemId
			: this._supplierItem ? this._supplierItem.id
				: this._adOrderingItem ? this._adOrderingItem.supplierItem.id
					: 0;
	}

	@computed get supplierId() : number {
		if (this._supplierItem) return this._supplierItem.supplierId;
		const t = this._helper.templateRepo.byId(this._templateItem!.templateId);
		return t ? t.supplierId : 0;
	}

	@computed get supplier() : ISupplier | undefined {
		return this._helper.supplierRepo.byId(this.supplierId);
	}

	@computed get supplierName() : string {
		const s = this.supplier;
		return s ? s.name : '?';
	}

	@computed get retailPrice() : number | undefined {
		return this._templateItem && this._templateItem.retailPrice;
	}

	@computed get listPrice() : number | undefined {
		return this._templateItem && this._templateItem.listPrice ? this._templateItem.listPrice : this._supplierItem!.price;
	}

	@computed get amountToOrder() {
		return this._templateItem ? this._templateItem.amountToOrder : this._adOrderingItem ? this._adOrderingItem.amountToOrder : 0;
	}
}

interface ISupplierInfo {
	id: number
	name: string
	supplier?: ISupplier,
	nPricedItems: number
	nUnpricedItems: number
	priceSum: number
	priceText: string
}

export const OrderingStore = types.model('OrderingStore', {
	templDetailsStore: TemplDetailsStore,
})
	.views(self => ({
		get omnistore() : IOmnistore {
			return getOmnistore();
		},
	}))
	.views(self => ({
		get multirepo() {
			return self.omnistore.multirepo;
		},
		get locationStore() {
			return self.omnistore.locationStore;
		},
	}))
	.views(self => ({
		get templates() : Array<ITemplate> {
			const selectedSupplierId = self.locationStore.supplierId;
			return self.multirepo.templateRepo.allAsList
				.filter(t => (!selectedSupplierId || t.supplierId===selectedSupplierId) && t.branchId==self.locationStore.branchId);
		},
	}))
	.views(self => ({
		get suppliers() : Array<ISupplier> {
			return self.templates.map(t => self.multirepo.supplierRepo.byId(t.supplierId)).filter(mg.isPresent);
		},
	}))
	.views(self => ({
		get uniqueTemplate() : ITemplate | undefined {
			const a = self.templates;
			return a.length==1 ? a[0] : undefined;
		},
		get uniqueTemplateId() : number { //TODO remove
			const a = self.templates;
			return a.length==1 ? a[0].id : 0;
		},
		get uniqueSupplierId() : number { //TODO remove
			const a = self.suppliers;
			return a.length==1 ? a[0].id : 0;
		},
	}))
	.views(self => ({
		get templateItemStores() {
			return self.templates.map(t => self.multirepo.templateItemRepo(t.id));
		},
		get offerStores() {
			return self.locationStore.page !== 'SupplierOffers' ? [] : self.suppliers.map(s => self.multirepo.specialOfferRepo(s.id));
		},
		get catalogStores() {
			return self.locationStore.page !== 'LCatalog' ? [] : self.suppliers.map(s => self.multirepo.supplierItemRepo(s.id));
		},
		get adOrderingItems() {
			return self.locationStore.page !== 'Checkout' ? [] : self.templates.flatMap(t => adOrderingStore.byTemplateId(t.id));
		},
	}))
	.views(self => ({
		get usedStores() {
			return [self.multirepo.templateRepo, self.multirepo.supplierRepo, ...self.templateItemStores, ...self.offerStores, ...self.catalogStores];
		},
		get combiItemHelper() {
			const supplierRepo = self.multirepo.supplierRepo;
			const templateRepo = self.multirepo.templateRepo;
			const itemCustomizationRepo = self.multirepo.itemCustomizationRepo;
			return new CombiItemHelper(supplierRepo, templateRepo, itemCustomizationRepo, self.locationStore);
		},
	}))
	.views(self => ({
		get combiItems() : Array<CombiItem> {
			const tiMap = new Map<number, ITemplateItem>();
			self.templateItemStores.flatMap(s => s.allAsList).forEach(ti => tiMap.set(ti.supplierItemId, ti));

			const siMap = new Map<number, ISupplierItem>();
			self.offerStores.flatMap(s => s.allAsList).forEach(si => siMap.set(si.id, si));
			self.catalogStores.flatMap(s => s.allAsList).forEach(si => siMap.set(si.id, si));

			const keys = new Set<number>([...tiMap.keys(), ...siMap.keys()]);
			const result: Array<CombiItem> = [];

			const helper = self.combiItemHelper;
			for (const k of keys) result.push(new CombiItem(tiMap.get(k), siMap.get(k), undefined, helper));
			for (const i of self.adOrderingItems) result.push(new CombiItem(undefined, i.supplierItem, i, helper));
			return result;
		},
	}))
	.views(self => ({
		get supplierInfos() : Array<ISupplierInfo> {
			const map: Map<number, ISupplierInfo> =
			new Map(self.suppliers.map(s => [s.id, {id: s.id, name: s.name, supplier: s, nPricedItems: 0, nUnpricedItems: 0, priceSum: 0, priceText: ''}]));
			for (const i of self.combiItems) {
				if (!i.amountToOrder) continue;
				const value = map.get(i.supplierId);
				if (!mg.assertPresent(value, 'supplierInfos', i.supplierId, i)) continue;
				const price = i.retailPrice || i.listPrice;
				if (price) {
					value.nPricedItems += +i.amountToOrder;
					value.priceSum += i.amountToOrder * price;
				} else {
					value.nUnpricedItems += +i.amountToOrder;
				}
			}
			const result: Array<ISupplierInfo> = [...map.values()];
			result.sort(mg.comparatorOn(o => o.supplier!.name));
			if (result.length>1 && (self.locationStore.page!=='LCatalog' || self.omnistore.reoctoStore.isTester)) {
				result.unshift({
					id: 0,
					name: transl('allSuppliers'),
					nPricedItems: result.map((e: any) => e.nPricedItems).reduce((a, b) => a+b, 0),
					nUnpricedItems: result.map((e: any) => e.nUnpricedItems).reduce((a, b) => a+b, 0),
					priceSum: result.map((e: any) => e.priceSum).reduce((a, b) => a+b, 0),
					priceText: '',
				});
			}
			result.forEach(o => o.priceText = o.priceSum>0 ? 'EUR ' + o.priceSum.toFixed(3).replace('.', ',') : '');
			return result;
		},
		get hasPrice() {
			for (let e of self.combiItems) {
				if (e.retailPrice || e.listPrice) return true;
			}
			return false;
		},
	}))
	.actions(self => ({
		toggleSelectedSupplierId(id: number) {
			const s = self.locationStore;
			s.setSupplierId(id===s.supplierId && s.page==='Checkout' ? 0 : id);
		}
	}));

export function newOrderingStore(): Instance<typeof OrderingStore> {
	const templDetailsStore = TemplDetailsStore.create({});
	return OrderingStore.create({templDetailsStore});
}
